import type { FleetInvitationResponse, FleetV2Response } from '@emma/api_client/types';
import { Box, Flex, Icon, IconButton, Link, Text, useToast } from '@lego/klik-ui';
import { Trash } from '@lego/klik-ui/icons';
import { useMemo, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { DeviceAssignPopover } from '@/components/common/DeviceAssignPopover';
import { DetailsSection } from '@/components/common/details/DetailsSection';
import { TextHeader } from '@/components/common/headers/TextHeader';
import { deviceTableColumns } from '@/components/common/table/column_definitions/Device';
import { deviceEmptyMessage } from '@/components/common/table/deviceEmptyMessage';
import { TableStoreProvider } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import { useDevices } from '@/hooks/device';
import { useDeleteFleet, useFleet } from '@/hooks/fleet';
import { useAssignDevicesToFleet } from '@/hooks/fleet/fleet_batch_assignment_resource';
import { useCanWriteById } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';
import type { DetailSectionItem } from '../common/details/DetailsBox';

type OwnedFleetProps = {
  type: 'owned';
  fleet: FleetV2Response;
};

type InvitedFleetProps = {
  type: 'invited';
  invitation: FleetInvitationResponse;
  ownerWorkspaceName: string;
  invitedWorkspaceId: number;
};

type Props = OwnedFleetProps | InvitedFleetProps;

const fleetDevicesDescription =
  'Devices assigned to this fleet that will receive its active deployment';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: card has many conditional sections by design
export const FleetCard = (props: Props) => {
  const isInvited = props.type === 'invited';
  const toast = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const fleetId = isInvited ? props.invitation.fleetId : props.fleet.id;
  const ownerWorkspaceId = isInvited ? props.invitation.workspaceId : props.fleet.workspaceId;
  const workspaceIdForDevices = isInvited ? props.invitedWorkspaceId : props.fleet.workspaceId;

  const canWrite = useCanWriteById(ownerWorkspaceId);
  const deleteFleet = useDeleteFleet(ownerWorkspaceId, fleetId);

  const { data: fetchedFleetData } = useFleet(
    isInvited ? ownerWorkspaceId : null,
    isInvited ? fleetId : null
  );

  const fleet = isInvited ? fetchedFleetData : props.fleet;
  const fleetName = fleet?.name ?? `Fleet #${fleetId}`;

  const {
    data: fleetDevicesData,
    error: fleetDevicesError,
    isLoading: fleetDevicesLoading,
    mutate: mutateFleetDevices,
    isValidating: fleetDevicesValidating,
  } = useDevices({ fleetIdIn: [fleetId] });

  const {
    data: workspaceDevicesData,
    isLoading: workspaceDevicesLoading,
    error: workspaceDevicesError,
  } = useDevices({
    workspaceIdIn: [workspaceIdForDevices],
  });

  const batchAssignDevices = useAssignDevicesToFleet();

  const fleetDevices = fleetDevicesData?.devices ?? [];
  const workspaceDevices = workspaceDevicesData?.devices ?? [];

  const assignableDevices = useMemo(() => {
    const fleetDeviceIds = new Set(fleetDevices.map((d) => d.id));
    return workspaceDevices.filter((d) => !fleetDeviceIds.has(d.id)).sort((a, b) => a.id - b.id);
  }, [fleetDevices, workspaceDevices]);

  const handleAssign = async (
    deviceIds: number[]
  ): Promise<{ failedDeviceIds: number[] } | null> => {
    try {
      const result = await batchAssignDevices({
        fleetId,
        workspaceId: ownerWorkspaceId,
        deviceIds,
      });
      const succeededCount = result.succeeded.length;
      const failedCount = result.failed.length;
      if (failedCount === 0) {
        toast(
          ToastOptions.success(
            'Devices assigned',
            `Successfully assigned ${succeededCount} device${succeededCount !== 1 ? 's' : ''} to fleet`
          )
        );
      } else {
        toast(
          ToastOptions.error(
            'Partial assignment',
            `${succeededCount} assigned, ${failedCount} failed: ${result.failed.map((f) => `Device #${f.deviceId}: ${f.error}`).join('; ')}`
          )
        );
      }
      void mutateFleetDevices();
      return { failedDeviceIds: result.failed.map(({ deviceId }) => deviceId) };
    } catch {
      return null;
    }
  };

  const detailItems: Record<string, DetailSectionItem> = {
    'Fleet ID': { body: fleetId },
    ...(isInvited && {
      'Owner Workspace': {
        body: (
          <Link as={ReactRouterLink} to={paths.workspaceById(ownerWorkspaceId)}>
            {props.ownerWorkspaceName}
          </Link>
        ),
      },
    }),
    Description: { body: fleet?.description ?? '-' },
    'Active Deployment ID': {
      body:
        fleet?.activeDeploymentId != null ? (
          <Link
            as={ReactRouterLink}
            to={paths.deploymentById(ownerWorkspaceId, fleetId, fleet.activeDeploymentId)}
          >
            {fleet.activeDeploymentId}
          </Link>
        ) : (
          '-'
        ),
    },
  };

  const fleetDetailsSections = [{ items: detailItems }];

  return (
    <Box backgroundColor={'transparent'} width={'100%'}>
      <Box background="white" boxShadow="md" padding={6} rounded="md" width={{ base: '100%' }}>
        <Flex alignItems="center" justifyContent="space-between" mb={4}>
          <Flex alignItems="center" gap={2}>
            <Link as={ReactRouterLink} p={0} to={paths.fleetById(ownerWorkspaceId, fleetId)}>
              <TextHeader tagLevel="h3" title={fleetName} />
            </Link>
            <Text color="gray" fontSize="xs" fontWeight="medium">
              Details
            </Text>
          </Flex>
          {canWrite && !isInvited && (
            <IconButton
              aria-label="Delete fleet"
              colorScheme="error"
              icon={<Icon as={Trash} />}
              onClick={() => setIsDeleteDialogOpen(true)}
              size="sm"
              variant="ghost"
            />
          )}
        </Flex>

        {fleetDetailsSections.map((section) => (
          <DetailsSection key="fleet-details" section={section} />
        ))}

        <TableStoreProvider
          initialColumnVisibility={{
            fleet: false,
            workspace: !isInvited,
          }}
          initialSorting={[{ desc: true, id: 'id' }]}
          name={`FleetDevices-${fleetId}`}
        >
          <Table
            addButton={
              canWrite
                ? () => (
                    <DeviceAssignPopover
                      assignableDevices={assignableDevices}
                      hasError={Boolean(workspaceDevicesError)}
                      isLoadingDevices={workspaceDevicesLoading}
                      onAssign={handleAssign}
                      tooltipLabel={
                        isInvited
                          ? 'Assign devices from your workspace to this fleet'
                          : 'Assign devices to the fleet'
                      }
                    />
                  )
                : undefined
            }
            columns={deviceTableColumns}
            count={fleetDevicesData?.itemCount ?? 0}
            data={fleetDevices}
            description={fleetDevicesDescription}
            emptyMessage={deviceEmptyMessage(
              fleetDevicesData?.itemCount,
              'No devices in this fleet'
            )}
            filteredEmptyMessage="No devices match the filters"
            isLoading={Boolean(fleetDevicesLoading || !fleetDevicesData || fleetDevicesError)}
            isValidating={fleetDevicesValidating}
            nestedStyling
            refresh={mutateFleetDevices}
            rowLink={({ id }) => paths.deviceById(id)}
            title="Devices"
          />
        </TableStoreProvider>
      </Box>
      {canWrite && !isInvited && (
        <ConfirmationDialog
          body="Are you sure you want to delete this fleet? This action cannot be undone. Only empty fleets (no devices or deployments) can be deleted."
          header="Delete Fleet"
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirmed={() => {
            setIsDeleteDialogOpen(false);
            // Already toasted and logged by the mutation hook; caught only to
            // avoid an unhandled promise rejection here.
            void deleteFleet().catch(() => {});
          }}
        />
      )}
    </Box>
  );
};
